import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import BlockIcon from '@mui/icons-material/Block'; // Barrier
import CategoryIcon from '@mui/icons-material/Category'; // Generic Type
import ConstructionIcon from '@mui/icons-material/Construction'; // Logistical
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoIcon from '@mui/icons-material/Info'; // Informational
import KeyIcon from '@mui/icons-material/Key'; // Opportunity
import PsychologyIcon from '@mui/icons-material/Psychology'; // Behavioral
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest'; // Systemic
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

// Helper for Barrier Type
const getBarrierTypeStyle = (type) => {
  switch (type?.toLowerCase()) {
    case 'informational':
      return { icon: <InfoIcon fontSize="small" />, color: 'info', label: 'Informational' };
    case 'logistical':
      return { icon: <ConstructionIcon fontSize="small" />, color: 'warning', label: 'Logistical' };
    case 'behavioral':
      return { icon: <PsychologyIcon fontSize="small" />, color: 'secondary', label: 'Behavioral' };
    case 'systemic':
      return { icon: <SettingsSuggestIcon fontSize="small" />, color: 'error', label: 'Systemic' };
    default:
      return { icon: <CategoryIcon fontSize="small" />, color: 'default', label: type || 'N/A' };
  }
};

const OutcomeBarrierAnalysisTranscript = ({ summaryData }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!summaryData || !summaryData.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Outcome Barrier analysis data not available.
        </Box>
      </Card>
    );
  }

  const {
    summary,
    most_common_barriers = [],
    high_leverage_opportunities = [],
    entries = [],
  } = summaryData.results;

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Outcome & Barrier Analysis
          <AnalysisLensHelperText lensName="outcome_barrier" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        {summary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Analysis Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          </Box>
        )}

        {/* Common Barriers & Opportunities */}
        {(most_common_barriers.length > 0 || high_leverage_opportunities.length > 0) && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
            {most_common_barriers.length > 0 && (
              <Box sx={{ flex: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <BlockIcon color="error" fontSize="small" /> Most Common Barriers
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {most_common_barriers.map((barrier, idx) => (
                    <Chip
                      key={`barrier-${idx}`}
                      label={barrier}
                      size="small"
                      variant="outlined"
                      color="error"
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {high_leverage_opportunities.length > 0 && (
              <Box sx={{ flex: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <KeyIcon color="success" fontSize="small" /> High-Leverage Opportunities
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {high_leverage_opportunities.map((opp, idx) => (
                    <Chip
                      key={`opp-${idx}`}
                      label={opp}
                      size="small"
                      variant="outlined"
                      color="success"
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}

        {/* Outcome Entries */}
        {entries.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Desired Outcomes & Associated Barriers
            </Typography>
            {entries.map((entry, index) => {
              const panelId = `panel${index}`;
              const barrierTypeStyle = getBarrierTypeStyle(entry.barrier_type);
              return (
                <Accordion
                  key={panelId}
                  expanded={expanded === panelId}
                  onChange={handleChange(panelId)}
                  TransitionProps={{ unmountOnExit: true }}
                  sx={{
                    '&:before': { display: 'none' },
                    border: 1,
                    borderColor: 'divider',
                    '&:not(:last-child)': { borderBottom: 0 },
                    borderRadius: 1,
                    mb: 1.5,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${panelId}-content`}
                    id={`${panelId}-header`}
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 'inherit',
                      borderBottomLeftRadius: expanded === panelId ? 0 : 'inherit',
                      borderBottomRightRadius: expanded === panelId ? 0 : 'inherit',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 'medium', flexGrow: 1, pr: 2 }}
                    >
                      Outcome: {entry.outcome}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Stack spacing={2}>
                      {/* Barriers */}
                      {entry.barriers?.length > 0 && (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                              flexWrap: 'wrap',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'medium',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <BlockIcon fontSize="inherit" color="error" /> Barriers:
                            </Typography>
                            <Chip
                              icon={barrierTypeStyle.icon}
                              label={`Type: ${barrierTypeStyle.label}`}
                              color={barrierTypeStyle.color}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          <List dense disablePadding sx={{ pl: 1 }}>
                            {entry.barriers.map((barrier, idx) => (
                              <ListItem key={`entry-barrier-${index}-${idx}`} disablePadding>
                                <ListItemText
                                  primary={`• ${barrier}`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Quotes */}
                      {entry.quotes?.length > 0 && (
                        <Box sx={{ pt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Supporting Quotes:
                          </Typography>
                          <List dense disablePadding>
                            {entry.quotes.map((quote, idx) => (
                              <ListItem
                                key={`entry-quote-${index}-${idx}`}
                                disableGutters
                                sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                              >
                                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                  <FormatQuoteIcon
                                    fontSize="small"
                                    sx={{ transform: 'scaleX(-1)' }}
                                    color="disabled"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={quote}
                                  primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default OutcomeBarrierAnalysisTranscript;
